import {map, mergeMap} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {BlobId, DeviceSlotId, ProjectId, RoleId, UserId,} from '../../types/ids';
import {Injectable} from '@angular/core';
import {AuthService} from '../../services/auth.service';
import {ProjectInfo} from '../../types/projectInfo';
import {isDefined} from '../../utils/utils';
import {Project} from '../../types/interfaces/project.interface';
import {GenericApiResponse} from '../apibase.api';
import { HttpClient } from '@angular/common/http';
import {AllGrantedRoles, Role, RoleIdsByUser, RolesByProject} from '../../types/user/role';
import {Permission} from '../../types/user/permission';
import {BusinessTransaction, businessTransactionFromBackendObject} from '../../types/business-transaction';
import {User, userFromBackendObject} from '../../types/user/user';
import {OsUpdateAPI} from '../../types/interfaces/os-update.interface';
import {AuthProviderInstance, AuthProviderUpdateDto} from '@webmate-web-core/shared/types/auth';
import {DNSEntry} from '@webmate-web-core/shared/types/dns-entry';
import {ProjectNotes} from '@webmate-web-core/shared/services/project.service';
import {AdminFeatureMap, Feature, FeatureConfigFieldOptions, FeatureId, FeatureMap} from '@webmate-web-core/shared/types/feature';

/**
 * HTTP access to webmate Customer Administration.
 */
@Injectable({
    providedIn: "root"
})
export class AdminApi {
    constructor(public OAuth2AuthService: AuthService, private http: HttpClient) {}

    inviteUser(name: string, email: string, projects?: ProjectId[], roles?: RoleId[]): Observable<GenericApiResponse> {
        let req = {name: name, email: email, projects: projects, roles: roles};
        return this.http.post<GenericApiResponse>("/administration/users/invite", req);
    }

    inviteBulkUser(users: {name: string, email: string}[], projects?: ProjectId[], roles?: RoleId[]): Observable<GenericApiResponse> {
        let req = {userInfo: users, projects: projects, roles: roles};
        return this.http.post<GenericApiResponse>("/administration/users/invite/bulk", req);
    }

    deleteUser(userId: UserId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/administration/users/" + userId);
    }

    deleteUsersBulk(userIds: Array<UserId>) {
        const request = {
            type: 'DeleteUsersBulk',
            users: userIds
        };
        return this.http.post<GenericApiResponse>("/administration/users/bulk", request);
    }

    updateUser(userId: UserId, name: string, email?: string, properties?: any): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/administration/users/" + userId, {name: name, email: email, properties: properties || {}});
    }

    activateUserAccount(userId: UserId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/administration/users/" + userId + "/activate", {});
    }

    setUsersActiveBulk(userIds: Array<UserId>, active: boolean) {
        const request = {
            type: 'ChangeUsersAccountStateBulk',
            users: userIds,
            active
        };
        return this.http.post<GenericApiResponse>("/administration/users/bulk", request);
    }

    resendInvitationBulk(userIds: Array<UserId>) {
        const request = {
            type: 'ResendInviteBulk',
            users: userIds
        };
        return this.http.post<GenericApiResponse>("/administration/users/bulk", request);
    }

    updateUserPropertiesBulk(userIds: Array<UserId>, properties: any) {
        const request = {
            type: 'UpdateUserPropertiesBulk',
            users: userIds,
            properties: properties
        };
        return this.http.post<GenericApiResponse>("/administration/users/bulk", request);
    }

    getRoleById(roleId: RoleId): Observable<Role> {
        return this.http.get<Role>("/administration/roles/" + roleId);
    }

    deactivateUserAccount(userId: UserId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/administration/users/" + userId + "/deactivate", {});
    }

    getRolesOfCurrentCustomer(): Observable<Array<Role>> {
        return this.http.get<Array<Role>>("/administration/roles");
    }

    getRolesForUserInProject(userId: UserId, projectId: ProjectId): Observable<Array<Role>> {
        return this.http.get<Array<Role>>(`/administration/users/${userId}/projects/${projectId}/grantedroles`);
    }

    getRolesForAllUsersInProject(projectId: ProjectId): Observable<RoleIdsByUser> {
        return this.http.get<RoleIdsByUser>(`/administration/projects/${projectId}/grantedroles`);
    }

    getRolesForUserByProject(userId: UserId): Observable<RolesByProject> {
        return this.http.get<RolesByProject>("/administration/users/" + userId + "/projects/grantedroles");
    }

    getAllGrantedRoles(): Observable<AllGrantedRoles> {
        return this.http.get<AllGrantedRoles>("/administration/grantedroles");
    }

    setRolesForUserInProject(userId: UserId, projectId: ProjectId, newRoles: Array<RoleId>): Observable<GenericApiResponse> {
        let o = {
            newRoles: newRoles,
            project: projectId
        };
        return this.http.put<GenericApiResponse>("/administration/users/" + userId + "/grantedroles", o);
    }

    getAllUsersForCustomer(): Observable<UserId[]> {
        return this.http.get<Array<UserId>>("/administration/users");
    }

    getAllUserProfilesForCustomer(): Observable<User[]> {
        return this.http.get<Array<User>>("/administration/userprofiles").pipe(map(response => response.map(profile => userFromBackendObject(profile))));
    }

    getManageablePermissions(): Observable<Permission[]> {
        return this.http.get<Array<Permission>>("/administration/permissions");
    }

    getManageableOrgPermissions(): Observable<Permission[]> {
        return this.http.get<Array<Permission>>("/administration/org-permissions");
    }

    getProjectsUserIsMemberOf(userId: UserId): Observable<ProjectId[]> {
        return this.http.get<Array<ProjectId>>("/administration/users/" + userId + "/projects");
    }

    addUserToProject(userId: UserId, projectId: ProjectId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/administration/projects/" + projectId + "/users/" + userId, {});
    }

    removeUserFromProject(userId: UserId, projectId: ProjectId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/administration/projects/" + projectId + "/users/" + userId);
    }

    getProjectsOfCustomer(): Observable<Project[]> {
        return this.http.get<Array<Project>>("/administration/projects?minimized=false");
    }

    getProjectsOfCustomerMinimized(): Observable<ProjectInfo[]> {
        return this.http.get<Array<any>>("/administration/projects?minimized=true");
    }

    addRole(name: string, desc: string, ssoManaged: boolean, permissions: string[]): Observable<Role> {
        let o = {
            name: name,
            permissions: permissions,
            ssoManaged: ssoManaged,
            desc: desc
        };
        return this.http.post<Role>("/administration/roles", o);
    }

    setAdministrativeAccessForUsersBulk(userIds: Array<UserId>, allowAdminAccess: boolean) {
        const request = {
            type: 'SetUsersAdminStateBulk',
            users: userIds,
            isAdmin: allowAdminAccess
        };
        return this.http.post<GenericApiResponse>("/administration/users/bulk", request);
    }

    setAdministrativeAccess(userId: UserId, newValue: boolean): Observable<GenericApiResponse> {
        let o = {
            hasAccess: newValue,
        };
        return this.http.post<GenericApiResponse>("/administration/users/" + userId + "/administrative-access", o);
    }

    updateRole(roleId: RoleId, name: string, desc: string, ssoManaged: boolean, permissions: string[]): Observable<boolean> {
        let o = {
            name: name,
            permissions: permissions,
            ssoManaged: ssoManaged,
            desc: desc
        };
        return this.http.put<boolean>("/administration/roles/" + roleId, o);
    }

    deleteRole(roleId: RoleId): Observable<boolean> {
        return this.http.delete<boolean>("/administration/roles/" + roleId);
    }

    cancelBtx(btxId: string): Observable<GenericApiResponse> {
        let cmd = {
            action: "cancel"
        };
        return this.http.post<GenericApiResponse>("/btx/" + btxId, cmd);
    }

    getAllBtxForProject(projectId: ProjectId, count: string, after?: string, state?: string): Observable<BusinessTransaction[]> {
        let params = "?count=" + count;
        if (after)
            params = params + "&after=" + after;
        if (state && state !== 'all')
            params = params + "&state=" + state;
        return this.http.get<Array<any>>("/btx/" + projectId + "/btxs" + params).pipe(map(response => {
            return response.map(x => businessTransactionFromBackendObject(x))
        }));
    }

    getPermissionsForCurrentUserInProject(projectId: ProjectId): Observable<string[]> {
        return this.http.get<Array<string>>("/projects/" + projectId + "/permissions");
    }

    updateProjectProperties(projectId: ProjectId, newName: string, newDescription: string, newLogo: BlobId | null): Observable<GenericApiResponse> {
        let cmd: any = {};
        cmd["newName"] = isDefined(newName) ? newName : "[Unnamed Project]";
        cmd["newDescription"] = isDefined(newDescription) ? newDescription : "";
        if (isDefined(newLogo)) cmd["newLogo"] = newLogo;
        return this.http.post<GenericApiResponse>("/projects/" + projectId + "/update", cmd);
    }

    updateProjectNotes(projectId: ProjectId, newNotes: ProjectNotes[]): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/projects/" + projectId + "/notes", {notes: newNotes});
    }

    deleteProject(projectId: ProjectId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/administration/projects/" + projectId);
    }

    createNewProject(name: string, description: string, slots: DeviceSlotId[], roleMapping?: any[], parent?: ProjectId): Observable<ProjectId> {
        let request: any = {
            name: name,
            description: description,
            slots: slots
        };
        if (isDefined(roleMapping)) {
            request['userIdsToRoles'] = roleMapping;
        }
        if (isDefined(parent)) {
            return this.http.post<any>("/administration/projects/" + parent + "/subprojects", request);
        } else {
            return this.http.post<any>("/administration/projects/subprojects", request);
        }
    }

    setRedeploymentTime(projectId: ProjectId, hour: number, minute: number): Observable<GenericApiResponse> {
        let time = {
            hour: hour,
            minute: minute
        };
        return this.http.post<GenericApiResponse>("/projects/" + projectId + "/daily_redeployment", time);
    }

    downloadUserInfoAsCSV(): Observable<Blob> {
        return this.http.get("/administration/users/exportascsv", {responseType: 'blob'});
    }


    setOSUpdateConfig(config: OsUpdateAPI): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>("/administration/general/osupdate", config);
    }

    getOSUpdateConfig(): Observable<OsUpdateAPI> {
        return this.http.get<OsUpdateAPI>("/administration/general/osupdate");
    }

    /**
     * Retrieve AuthProviderInstances (used for SSO) from the backend.
     */
    getSSOConfig(): Observable<AuthProviderInstance[]> {
        return this.http.get<AuthProviderInstance[]>("/administration/auth/authprovider");
    }

    storeSSOConfig(authProviderUpdateDto: AuthProviderUpdateDto): Observable<any> {
        return this.http.post("/administration/auth/authprovider", authProviderUpdateDto);
    }

    getAllFeatures(): Observable<AdminFeatureMap> {
        return this.http.get<AdminFeatureMap>("/administration/features");
    }

    getFeatureOverrides(): Observable<FeatureMap> {
        return this.http.get<FeatureMap>('/administration/featureOverrides');
    }

    getFeatureConfigFieldOptions(): Observable<FeatureConfigFieldOptions[]> {
        return this.http.get<FeatureConfigFieldOptions[]>('/administration/featureConfigFieldOptions');
    }

    setFeatureOverride(featureId: FeatureId, overrideValue: Feature): Observable<GenericApiResponse> {
        const body = {
            id: featureId,
            value: overrideValue
        };
        return this.http.post<GenericApiResponse>(`/administration/featureOverrides/${featureId}`, body);
    }

    deleteFeatureOverride(featureId: FeatureId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/administration/featureOverrides/${featureId}`);
    }

    getDnsEntries(): Observable<DNSEntry[]> {
        return this.http.get<{entries: DNSEntry[]}>("/administration/dns").pipe(map(x => x.entries));
    }

    setDnsEntries(entries: DNSEntry[]): Observable<any> {
        return this.http.post("/administration/dns", {entries});
    }
}
